import { FC } from 'react';
import { RolePrimeDerivee } from 'api/guarantee';

/** Taux des primes dérivées d'une branche, en pourcentage de l'assiette (20 = 20 %). */
export interface DerivedPremiumRates {
  catNatRate: number;
  catTechRate: number;
  attackRate: number;
}

/** Les trois rôles dont la prime est calculée à partir de l'assiette. */
export const DERIVED_ROLES: RolePrimeDerivee[] = [
  RolePrimeDerivee.CatNat,
  RolePrimeDerivee.CatTech,
  RolePrimeDerivee.Attack,
];

export const isDerivedRole = (role?: RolePrimeDerivee): boolean =>
  Boolean(role && DERIVED_ROLES.includes(role));

/** Nom court de chaque rôle dérivé, repris par les badges et la page branche. */
export const DERIVED_ROLE_NAMES: Record<string, string> = {
  [RolePrimeDerivee.CatNat]: 'Cat Nat',
  [RolePrimeDerivee.CatTech]: 'Cat Tech',
  [RolePrimeDerivee.Attack]: 'Attentat',
};

/** Taux de la branche qui s'applique à un rôle dérivé. */
export const rateOfRole = (
  role: RolePrimeDerivee,
  rates?: DerivedPremiumRates
): number | undefined => {
  if (!rates) return undefined;
  switch (role) {
    case RolePrimeDerivee.CatNat:
      return rates.catNatRate;
    case RolePrimeDerivee.CatTech:
      return rates.catTechRate;
    case RolePrimeDerivee.Attack:
      return rates.attackRate;
    default:
      return undefined;
  }
};

/** Pourcentage à la française : « 0,5 % ». */
export const formatRate = (rate: number): string =>
  `${rate.toLocaleString('fr-FR', { maximumFractionDigits: 3 })} %`;

/**
 * Rôle d'une garantie dans les primes dérivées (Cat Nat, Cat Tech, Attentat) de sa branche.
 *
 * Une seule assiette pour les trois primes : la somme des primes pures des garanties
 * « dans l'assiette ». Les garanties dérivées n'y entrent jamais.
 */
export const DERIVED_PREMIUM_ROLE_OPTIONS: {
  value: RolePrimeDerivee;
  label: string;
  hint: string;
}[] = [
  {
    value: RolePrimeDerivee.None,
    label: 'Hors calcul',
    hint: "Sa prime n'entre dans aucun calcul dérivé. Ex. : RC, défense recours, protection juridique.",
  },
  {
    value: RolePrimeDerivee.Base,
    label: "Dans l'assiette",
    hint: "Sa prime pure est additionnée aux autres garanties de l'assiette. Ex. : incendie, DDE, vol, climatique, dom. élec.",
  },
  {
    value: RolePrimeDerivee.CatNat,
    label: 'Est la garantie Cat Nat',
    hint: 'Sa formule de tunnel est ignorée : prime = taux × assiette.',
  },
  {
    value: RolePrimeDerivee.CatTech,
    label: 'Est la garantie Cat Tech',
    hint: 'Sa formule de tunnel est ignorée : prime = taux × assiette.',
  },
  {
    value: RolePrimeDerivee.Attack,
    label: 'Est la garantie Attentat',
    hint: 'Sa formule de tunnel est ignorée : prime = taux × assiette. Distinct du frais « Contribution attentat » (FGTI).',
  },
];

/**
 * Badge du rôle : bleu pour l'assiette, orange avec le taux de la branche pour une
 * garantie dérivée, rien pour une garantie hors calcul.
 */
export const DerivedPremiumRoleBadge: FC<{
  role?: RolePrimeDerivee;
  rates?: DerivedPremiumRates;
}> = ({ role, rates }) => {
  if (role === RolePrimeDerivee.Base) {
    return (
      <span className="flex-shrink-0 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-700">
        Dans l'assiette
      </span>
    );
  }

  if (!role || !isDerivedRole(role)) return null;

  const rate = rateOfRole(role, rates);
  return (
    <span className="flex-shrink-0 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
      {DERIVED_ROLE_NAMES[role]}
      {rate !== undefined && ` · ${formatRate(rate)}`}
    </span>
  );
};

export const DamageBadge: FC<{ value?: boolean }> = ({ value }) =>
  value ? (
    <span className="flex-shrink-0 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
      Dommages
    </span>
  ) : null;
